/*
 * Invoice, quote and payment rules (draft 3). Small pure functions, so the server, the web screens and the phone app all
 * get the same totals, tips, due times and edit-after-paid answers. The WordPress Steward (invpay.php, monthend.php,
 * invmerge.php, autobill.php) is the spec; BUILD-SPEC 3.7 is the law.
 */
import { formatMoney, type Cents, type Trade } from "./core.js";

// Invoices -------------------------------------------------------------------------------------------------------

/**
 * to_check: made from done visits, waiting for the office to look it over. scheduled: kept for a later send.
 * to_collect: waiting for an automatic card charge. sent: with the client. paid, no_charge and void are closed.
 */
export const INVOICE_STATUSES = ["to_check", "scheduled", "to_collect", "sent", "paid", "no_charge", "void"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/** Where an invoice shows in lists: the tabs of the Invoices page. */
export const INVOICE_STATES = ["to_check", "awaiting", "past_due", "debt", "paid", "closed"] as const;
export type InvoiceState = (typeof INVOICE_STATES)[number];

export const INVOICE_STATE_WORDS: Record<InvoiceState, string> = {
  to_check: "To check",
  awaiting: "Awaiting payment",
  past_due: "Past due",
  debt: "Debt",
  paid: "Paid",
  closed: "Closed",
};

/** Days the reminders wait after a client says a check is in the mail. */
export const CHECK_WAIT_DAYS = 10;

/**
 * Where an invoice sits in the main All list (owner, 2026-10-31), lowest first: To collect and To check (money now or
 * work to do), Past due and Debt, Payment on the way, Awaiting payment, Paid, then Closed (archived out of All). The
 * server's SQL order matches it.
 */
export function invoiceRank(i: { state: InvoiceState; status: string; onTheWay: boolean }): number {
  if (i.status === "to_collect") return 0;
  if (i.state === "to_check") return 1;
  if (i.state === "past_due" || i.state === "debt") return 2;
  if (i.state === "awaiting") return i.onTheWay ? 3 : 4;
  return i.state === "paid" ? 5 : 6;
}

/** The list tab an invoice is in right now. */
export function invoiceState(
  inv: { status: string; balanceCents: number; dueAt: Date | null; debt: boolean },
  now: Date,
): InvoiceState {
  if (inv.status === "to_check" || inv.status === "scheduled") return "to_check";
  if (inv.status === "paid") return "paid";
  if (inv.status === "void" || inv.status === "no_charge") return "closed";
  if (inv.debt) return "debt";
  if (inv.balanceCents > 0 && inv.dueAt && inv.dueAt.getTime() <= now.getTime()) return "past_due";
  return "awaiting";
}

/** A business is a client with a company name; they get days to pay, homes get hours. */
export function isBusiness(client: { companyName: string | null; isCompany?: boolean }): boolean {
  return Boolean(client.companyName?.trim()) || client.isCompany === true;
}

/**
 * When a sent invoice turns past due. Due on receipt; past due after 24 hours for homes and 7 days for businesses (the
 * company's settings), or the job's own due days when it has them.
 */
export function pastDueAt(
  sentAt: Date,
  business: boolean,
  jobDueDays: number | null,
  rule: { pastDueHoursHome: number; pastDueDaysBusiness: number },
): Date {
  const hours = jobDueDays !== null ? jobDueDays * 24 : business ? rule.pastDueDaysBusiness * 24 : rule.pastDueHoursHome;
  return new Date(sentAt.getTime() + hours * 3_600_000);
}

/** A client's payment terms in words, like Jobber's "Residential default (Due upon receipt)". */
export function paymentTermsWords(business: boolean, rule: { pastDueHoursHome: number; pastDueDaysBusiness: number }): string {
  const hours = business ? rule.pastDueDaysBusiness * 24 : rule.pastDueHoursHome;
  const when =
    hours === 0
      ? "due on receipt"
      : hours % 24 === 0 && hours > 24
        ? `due in ${String(hours / 24)} days`
        : `due in ${String(hours)} hours`;
  return `${business ? "Business" : "Home"} default (${when})`;
}

/** Totals of an invoice. The tip is its own amount, never a line, so payroll never counts it as work. */
export function invoiceTotals(lines: readonly { amountCents: number }[], tipCents: number, paidCents: number) {
  const subtotalCents = lines.reduce((s, l) => s + l.amountCents, 0);
  const totalCents = subtotalCents + tipCents;
  return { subtotalCents, totalCents, balanceCents: totalCents - paidCents };
}

// Tips -----------------------------------------------------------------------------------------------------------

/** The tip buttons. Nothing is picked until the client picks, and there is no "No tip" button. */
export const TIP_PERCENTS = [10, 15, 20] as const;

/** Tips show on the pay page when the company has them on, never for the agency. */
export function tipsOn(trade: Trade | (string & {}), settingOn: boolean): boolean {
  return trade !== "agency" && settingOn;
}

/** A percent of an amount in whole cents, rounded half up. */
export function percentOf(cents: number, percent: number): Cents {
  return Math.round((cents * percent) / 100) as Cents;
}

/** The tip buttons on the pay page: the company's three percents (Settings, Invoices), 10, 15 and 20 unless changed. */
export function tipChoices(
  subtotalCents: number,
  percents: readonly number[] = TIP_PERCENTS,
): { percent: number; cents: Cents }[] {
  return percents.map((percent) => ({ percent, cents: percentOf(subtotalCents, percent) }));
}

/** A client may add a tip on the pay page up to this many days after the invoice was paid (old tips.php). */
export const TIP_AFTER_DAYS = 14;

/** Until when a paid invoice with no tip can still take one (ISO time), or null. Tips must be on. */
export function tipUntil(p: { paidAt: Date | null; tipCents: number; tipsOn: boolean; now?: Date }): string | null {
  if (!p.tipsOn || p.tipCents > 0 || !p.paidAt) return null;
  const until = new Date(p.paidAt.getTime() + TIP_AFTER_DAYS * 86_400_000);
  return until > (p.now ?? new Date()) ? until.toISOString() : null;
}

/** The most a tip may be: the invoice itself or $500, whichever is more, so a typo cannot charge thousands. */
export function maxTip(subtotalCents: number): number {
  return Math.max(subtotalCents, 50_000);
}

// Messages -------------------------------------------------------------------------------------------------------

/** Fills {brand}, {first} and {job} in a message the company wrote. Unknown names stay as typed. */
export function fillTemplate(text: string, vars: { brand: string; first: string; job: string }): string {
  return text.replace(/\{(brand|first|job)\}/g, (_, k: "brand" | "first" | "job") => vars[k]);
}

/** The invoice email subject, starting with a capital ("{job} invoice from {brand}" is "Lawn care invoice from ..."). */
export function invoiceSubject(text: string, vars: { brand: string; first: string; job: string }): string {
  const s = fillTemplate(text, vars);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * The invoice text, as the old Steward sent it (steward_invoice_parts): the company's line, a blank line, then
 * "Pay your invoice here:" with the link on its own line.
 */
export function invoiceMessage(text: string, vars: { brand: string; first: string; job: string }, link: string): string {
  return `${fillTemplate(text, vars)}\n\nPay your invoice here:\n${link}`;
}

/**
 * What a quote is for, in the quote text (the old steward_quote_words): the title without a price, a note in brackets
 * or how often ("Lawn care every two weeks" is "Lawn care"), the parts joined with "and". Empty when nothing is left.
 */
export function quoteWords(title: string | null | undefined): string {
  const t = (title ?? "")
    .trim()
    .replace(/^walkthrough:\s*/i, "")
    .replace(/\s*(?:[:(]|\$\s*\d).*$/s, "")
    .trim();
  const often = /^(?:not (?:sure|priced)\b|every\b|weekly\b|bi-?weekly\b|monthly\b|daily\b|once\b|one[- ]time\b)/i;
  const keep = t
    .split(/\s*[,;+]\s*/)
    .map((p) => p.replace(/\s+(?:every|weekly|bi-?weekly|monthly|daily|once|one[- ]time)\b.*$/i, "").trim())
    .filter((p) => p !== "" && !often.test(p));
  const w = keep.length > 1 ? `${keep.slice(0, -1).join(", ")} and ${keep.at(-1) ?? ""}` : (keep[0] ?? "");
  return w.length <= 60 ? w : "";
}

/** The quote text, as the old Steward sent it (steward_quote_parts): the hello, a blank line, then the link line. */
export function quoteMessage(vars: { brand: string; first: string; title?: string | null }, link: string): string {
  const w = quoteWords(vars.title);
  return `Hi ${vars.first}, your ${w ? `${w} ` : ""}quote from ${vars.brand} is ready!\n\nView your quote here: ${link}`;
}

/** What the client calls the work in the confirm text, by trade. */
export function confirmWord(trade: string): string {
  return trade === "cleaning" ? "cleaning" : trade === "lawn" ? "lawn service" : trade === "washers" ? "wash" : "visit";
}

/**
 * The text a client gets to confirm a job the office booked (owner, 2026-10-30): the place and day, then sign on the link,
 * and save a card first when there is none on file.
 */
export function confirmMessage(
  vars: { brand: string; first: string; what: string; where: string | null; when: string; hasCard: boolean },
  link: string,
): string {
  const at = vars.where ? ` at ${vars.where}` : "";
  const ask = vars.hasCard ? "sign here" : "save a card and sign here";
  return `Hi ${vars.first}, this is ${vars.brand}. Please confirm your ${vars.what}${at} on ${vars.when}. To confirm, ${ask}.\n${link}`;
}

/**
 * A job still waiting on the client's confirm link (a job's extra): "unconfirmed" on the schedule, or the older way, held
 * off the schedule with waitingOk.
 */
export function jobUnconfirmed(extra: Record<string, unknown>): boolean {
  return typeof extra.confirmQuote === "string" && (extra.unconfirmed === true || Array.isArray(extra.waitingOk));
}

/** The street part of an address, for a short text. */
export function streetOf(address: string | null | undefined): string | null {
  const s = (address ?? "").split(",")[0]?.trim() ?? "";
  return s === "" ? null : s;
}

/** The day the card terms began (owner, 2026-11-02): saves before it showed the older sentence. */
export const CARD_TERMS_FROM = "2026-11-03";

/**
 * The sentence every card save point shows, with a Card terms link next to it. Saving the card is the client's OK to be
 * charged after each visit of a repeating job. `at` picks the words shown on that day (the dispute file).
 */
export function saveCardSentence(brand: string, at?: string | Date): string {
  const day = at === undefined ? null : (typeof at === "string" ? at : at.toISOString()).slice(0, 10);
  if (day !== null && day < CARD_TERMS_FROM) return `Save this card so ${brand} can charge it for future services.`;
  return `Save this card so ${brand} can charge it after each visit.`;
}

/** The Card terms the link opens on the pay, quote and save a card pages (owner, 2026-11-02: short, like Netflix). */
export const CARD_TERMS: readonly string[] = [
  "We keep your card on file with Stripe. We never see or store the full number.",
  "For repeat service, we charge your card after each visit.",
  "One-time jobs are charged only when you pay.",
  "The amount is the price on your plan or quote, plus any extras you ask for.",
  "You get a receipt by email each time.",
  "Not happy with a visit? Tell us within 24 hours and we will make it right.",
  "If a charge does not go through, we text you a link to pay.",
  "To stop charges after each visit, text or call us anytime.",
];

/**
 * How the pay page treats charges after each visit for this client: on (already agreed), offer (the brand counts paying
 * as the OK and says so), ask (the brand asks with a box first), none (no repeating job).
 */
export type AutoChargeOffer = "on" | "offer" | "ask" | "none";
export function autoChargeOffer(p: { clientOn: boolean; repeating: boolean; mode: "on" | "ask" }): AutoChargeOffer {
  if (p.clientOn) return "on";
  if (!p.repeating) return "none";
  return p.mode === "on" ? "offer" : "ask";
}

/** The pay page's words (owner, 2026-11-01): a card payment always keeps the card on file, and the page says so. */
export function cardKeptSentence(brand: string): string {
  return `Your card will be saved for future services with ${brand}.`;
}

// Edit after paid ------------------------------------------------------------------------------------------------

export const PAID_EDIT_CHOICES = ["credit", "no_credit"] as const;
export type PaidEditChoice = (typeof PAID_EDIT_CHOICES)[number];

export type PaidEdit =
  | { kind: "text" }
  | { kind: "up"; balanceCents: number }
  | { kind: "down" }
  | { kind: "credit"; creditCents: number }
  | { kind: "no_credit"; note: string }
  | { kind: "needs_choice"; overCents: number };

/**
 * What changing a paid invoice's total does. Same total: words only. Up: it reopens for the balance only. Down below what
 * was paid: the owner must say Credit (the difference goes on the client's account) or Don't credit (a note says why).
 * Payments already made are never changed.
 */
export function editPaid(p: {
  paidCents: number;
  fromTotalCents: number;
  toTotalCents: number;
  choice?: PaidEditChoice;
}): PaidEdit {
  if (p.toTotalCents === p.fromTotalCents) return { kind: "text" };
  if (p.toTotalCents > p.fromTotalCents) return { kind: "up", balanceCents: Math.max(0, p.toTotalCents - p.paidCents) };
  const over = p.paidCents - p.toTotalCents;
  if (over <= 0) return { kind: "down" };
  if (p.choice === "credit") return { kind: "credit", creditCents: over };
  if (p.choice === "no_credit")
    return {
      kind: "no_credit",
      note: `Paid ${formatMoney(p.paidCents as Cents)}, now ${formatMoney(p.toTotalCents as Cents)}. ${formatMoney(over as Cents)} not credited (refunded or settled another way).`,
    };
  return { kind: "needs_choice", overCents: over };
}

// Charging cards -------------------------------------------------------------------------------------------------

/**
 * May a visit's invoice be charged with no one pressing anything? Only for a repeating job, with auto charge on for the
 * client, the date they agreed in writing, and the job not turned off for it. One-time jobs never.
 */
export function canAutoCharge(p: {
  frequency: string;
  jobAutoPay: boolean;
  clientAutoCharge: boolean;
  agreedOn: string | null;
}): boolean {
  return p.frequency !== "one-time" && p.jobAutoPay && p.clientAutoCharge && p.agreedOn !== null;
}

/** Klarna (pay in 4) only for big one-time jobs: at or over this (the old ach.php, $300 to start). */
export const KLARNA_MIN_CENTS = 30_000;

/**
 * The ways a client may pay an invoice on the pay page (old ach.php): card always (with Apple Pay and Google Pay), a bank
 * account when the company (or this invoice) has bank on, and Klarna when it is on and the work is one-time, not charged
 * automatically, and at least the company's Klarna least (KLARNA_MIN_CENTS unless changed in Settings).
 */
export function payWaysFor(p: {
  ways: { card: boolean; bank: boolean; klarna: boolean };
  oneTime: boolean;
  autoCharge: boolean;
  balanceCents: number;
  /** The company's Klarna least, from Settings. */
  klarnaMinCents?: number;
}): ("card" | "bank" | "klarna")[] {
  const out: ("card" | "bank" | "klarna")[] = ["card"];
  if (p.ways.bank) out.push("bank");
  const least = p.klarnaMinCents ?? KLARNA_MIN_CENTS;
  if (p.ways.klarna && p.oneTime && !p.autoCharge && p.balanceCents >= least) out.push("klarna");
  return out;
}

/** The question the owner answers before a card is charged by hand. */
export function chargeQuestion(p: { client: string; cardBrand: string; last4: string; amountCents: number }): string {
  return `Charge ${p.client}'s ${p.cardBrand} ending ${p.last4} ${formatMoney(p.amountCents as Cents)}?`;
}

// Billing modes --------------------------------------------------------------------------------------------------

/** monthly jobs bill every 4 done visits on one invoice. */
export const VISITS_PER_MONTHLY_BILL = 4;

// Quotes ---------------------------------------------------------------------------------------------------------

export const QUOTE_STATUSES = ["draft", "sent", "approved", "converted", "declined", "expired"] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const QUOTE_STATUS_WORDS: Record<QuoteStatus, string> = {
  draft: "Draft",
  sent: "Waiting on client",
  approved: "Approved",
  converted: "Made into a job",
  declined: "Declined",
  expired: "Expired",
};

export interface Deposit {
  kind: "percent" | "amount";
  value: number;
}

/** A quote's totals: every line that is not optional, plus the optional ones picked; the deposit is a part of that. */
export function quoteTotals(
  lines: readonly { amountCents: number; optional: boolean; picked: boolean }[],
  deposit: Deposit | null,
) {
  const totalCents = lines.filter((l) => !l.optional || l.picked).reduce((s, l) => s + l.amountCents, 0);
  const depositCents = !deposit
    ? 0
    : Math.min(totalCents, deposit.kind === "percent" ? percentOf(totalCents, deposit.value) : Math.round(deposit.value));
  return { totalCents, depositCents };
}

// Payments -------------------------------------------------------------------------------------------------------

export const PAYMENT_METHODS = ["card", "bank", "klarna", "cash", "check", "credit", "crew", "zelle"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_WORDS: Record<PaymentMethod, string> = {
  card: "Card",
  bank: "Bank",
  klarna: "Klarna",
  cash: "Cash",
  check: "Check",
  credit: "Account credit",
  crew: "Paid to crew",
  zelle: "Zelle",
};

export const PAYMENT_STATUSES = ["succeeded", "pending", "failed", "refunded"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];
export const PAYMENT_STATUS_WORDS: Record<PaymentStatus, string> = {
  succeeded: "Succeeded",
  pending: "Pending",
  failed: "Failed",
  refunded: "Refunded",
};

/** How a payment came in, for the Payments list and page ("Paid through" on the old Steward). */
export function paidThroughWords(p: {
  method: PaymentMethod;
  online: boolean;
  crewCollected: boolean;
  refund: boolean;
  recordedBy: string | null;
}): string {
  if (p.refund) return p.recordedBy ? `Given back by ${p.recordedBy}` : "Given back";
  if (p.method === "credit") return "Account credit";
  if (p.crewCollected || p.method === "crew") return "Collected by the crew";
  if (p.online) return p.recordedBy ? `Saved card, charged by ${p.recordedBy}` : "Paid online";
  return p.recordedBy ? `Recorded by ${p.recordedBy}` : "Recorded in the Steward";
}

/**
 * A commercial bid's monthly price (FEATURE-MAP 9): the wage times 1.3 to 1.5, times the hours a visit, times visits a
 * week, times 52 weeks, divided by 12 months, plus supplies a month. Whole cents.
 */
export function commercialMonthlyCents(b: {
  wageCents: number;
  factor: number;
  hoursPerVisit: number;
  visitsPerWeek: number;
  suppliesCents?: number;
}): number {
  return Math.round((b.wageCents * b.factor * b.hoursPerVisit * b.visitsPerWeek * 52) / 12) + (b.suppliesCents ?? 0);
}
